/**
 * useBoothIdleTimer — drives the kiosk's idle-attract transition.
 *
 * The booth slips into BoothAttract when the launcher has been idle
 * for IDLE_MS milliseconds. Capture flows + QR continuity + theme
 * picker are considered "active" surfaces and do NOT count toward
 * idle — they pause the timer entirely so a mid-recording guest
 * never sees the attract screen.
 *
 * Every pointer/key event globally resets the idle clock — the
 * kiosk should feel unfailingly present while anyone is at it.
 *
 * Behaviour preserved verbatim from the previous inline timer in
 * BoothOSPage.js:
 *   • IDLE_MS = 45_000
 *   • Poll interval = 1500
 *   • Only ticks while view === 'launcher'
 *
 * @param {string} view     — current top-level booth view
 * @param {() => void} onIdle — invoked once the IDLE_MS threshold is met
 * @returns {{ bumpNow: () => void }} — call to manually reset the clock
 *   (used by view transitions / completion handlers).
 */
import { useEffect, useRef } from 'react';

const IDLE_MS = 45_000;
const POLL_MS = 1_500;

export function useBoothIdleTimer(view, onIdle) {
  const lastTouchRef = useRef(Date.now());

  // Reset the idle clock when the view changes, then start polling
  // ONLY when we're on the launcher. Off-launcher views (capture
  // flows, qr_continuity, theme_picker, attract) keep the clock
  // paused via this guard.
  useEffect(() => {
    if (view !== 'launcher') return undefined;
    // Visual-diff guardrail opt-out: when the Playwright suite
    // (yarn visual) is photographing the launcher we never want
    // the attract surface to interrupt the snapshot. The flag is
    // set in sessionStorage by the spec and only by the spec —
    // it is not user-reachable and never lives across sessions.
    try {
      if (typeof sessionStorage !== 'undefined' &&
          sessionStorage.getItem('booth_visual_test') === '1') {
        return undefined;
      }
    } catch {}
    lastTouchRef.current = Date.now();
    const id = setInterval(() => {
      if (Date.now() - lastTouchRef.current >= IDLE_MS) {
        onIdle();
      }
    }, POLL_MS);
    return () => clearInterval(id);
  }, [view, onIdle]);

  // Reset the idle clock on every pointer/key tick. Cheap & global.
  useEffect(() => {
    const bump = () => { lastTouchRef.current = Date.now(); };
    window.addEventListener('pointerdown', bump);
    window.addEventListener('keydown', bump);
    return () => {
      window.removeEventListener('pointerdown', bump);
      window.removeEventListener('keydown', bump);
    };
  }, []);

  return {
    bumpNow: () => { lastTouchRef.current = Date.now(); },
  };
}
